import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DoorOpen, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DECISION_TEMPLATES, fillTemplate } from "../lib/decisionTemplates";
import { SignaturePad } from "./SignaturePad";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ResignCountdown } from "./ResignCountdown";

const iso = (d) => d.toISOString().slice(0, 10);
const plus = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return iso(d); };
const TPL = DECISION_TEMPLATES.find((t) => t.key === "resign_request");

const Status = ({ r }) => {
  const s = r.resignation;
  if (s.status === "pending") return <p className="flex items-center gap-1.5 text-sm font-bold text-amber-700" data-testid="resign-status-pending"><Clock className="h-4 w-4" /> طلب استقالتك #{r.number} قيد المراجعة — آخر يوم مقترح {s.last_day}</p>;
  if (s.status === "accepted") return <div className="space-y-1" data-testid="resign-status-accepted"><p className="flex items-center gap-1.5 text-sm font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> تم قبول استقالتك — قرار #{s.accept_number}</p><p className="text-xs text-slate-500">ستجد قرار القبول في القائمة أدناه لتوقيعه.</p></div>;
  return <p className="flex items-center gap-1.5 text-sm font-bold text-red-700" data-testid="resign-status-rejected"><XCircle className="h-4 w-4" /> رُفض طلب الاستقالة{s.reject_reason ? `: ${s.reject_reason}` : ""}</p>;
};

const ResignForm = ({ emp, company, onClose, onDone }) => {
  const pad = useRef(null);
  const [reason, setReason] = useState("");
  const [last, setLast] = useState(plus(30));
  const [busy, setBusy] = useState(false);
  const html = useMemo(() => fillTemplate(TPL.body, emp, { company: company?.name || "الشركة", date: iso(new Date()), reason: reason.trim(), last_day: last }), [emp, company, reason, last]);
  const send = async () => {
    const signature = pad.current?.getSignature();
    if (!reason.trim()) { toast.error("اكتب سبب الاستقالة"); return; }
    if (!signature) { toast.error("وقّع في المربع"); return; }
    setBusy(true);
    try { await api.portalResign({ reason, last_day: last, body_html: html, signature }); toast.success("تم إرسال طلب الاستقالة للإدارة"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="resign-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">تقديم طلب استقالة</DialogTitle><DialogDescription>راجع نص الطلب ثم وقّع لإرساله للإدارة</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-slate-500">سبب الاستقالة</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="مثال: ظروف عائلية / فرصة عمل أخرى" data-testid="resign-reason" /></div>
          <div><label className="text-xs font-semibold text-slate-500">آخر يوم عمل (إشعار 30 يوماً افتراضياً — يمكن اختيار تاريخ سابق)</label>
            <Input type="date" value={last} onChange={(e) => setLast(e.target.value)} data-testid="resign-last-day" />
            {last && <div className="mt-1"><ResignCountdown day={last} /></div>}</div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-[13px] leading-7" data-testid="resign-preview" dangerouslySetInnerHTML={{ __html: html }} />
          <div><label className="text-xs font-semibold text-slate-500">توقيعك</label><SignaturePad ref={pad} height={130} /></div>
          <Button onClick={send} disabled={busy} className="w-full bg-[#0f1f3d]" data-testid="resign-submit">{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />}توقيع وإرسال الطلب</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PortalResignation = () => {
  const { user } = useAuth();
  const [req, setReq] = useState(undefined);
  const [open, setOpen] = useState(false);
  const load = () => api.portalResignation().then((r) => setReq(r.request)).catch(() => setReq(null));
  useEffect(() => { load(); }, []);
  if (req === undefined) return null;
  const canNew = !req || req.resignation?.status === "rejected";
  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3" data-testid="portal-resignation">
      {req && <Status r={req} />}
      {canNew && <button onClick={() => setOpen(true)} data-testid="resign-open" className="mt-1 flex items-center gap-1.5 text-sm font-bold text-slate-700 hover:text-red-700"><DoorOpen className="h-4 w-4" /> تقديم طلب استقالة</button>}
      {open && <ResignForm emp={user} company={user?.company} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
};
